import * as React from "react";
import {
  Stack,
  SearchBox,
  Fabric,
  DetailsList,
  SelectionMode,
  DetailsListLayoutMode,
  IColumn,
  TooltipHost,
  IDetailsListProps,
  IDetailsRowStyles,
  DetailsRow,
  Dropdown,
  DatePicker,
  DayOfWeek,
  PrimaryButton,
  DefaultButton,
} from "office-ui-fabric-react";
import { getAuthLogs, exportAuthLogsExcel } from "../../services/audit/AuditLogService";
import { IAuthLog, IAuthLogFilters } from "../../vm";
import { Loading } from "../../services/Loading";
import Icon from "@mdi/react";
import { mdiFilterOutline, mdiDownloadOutline } from "@mdi/js";
import { THEME } from "../../utils/Styles";
import { childrenGap, AUTH_EVENT_OPTIONS, AUTH_STATUS_OPTIONS, AUTH_EVENT_COLOR, AUTH_STATUS_COLOR, AUTH_EVENT_OPTIONS_DICT, AUTH_LOGIN_METHODS_DICT, AUTH_STATUS_OPTIONS_DICT } from "../common/Constants";
import CustomPagination from "../common/CustomPagination";
import NoDataTile from "../common/NoDataTile";
import moment from "moment";
import { ToastContext } from "../common/ToastProvider";

const AuthLogTab: React.FC = () => {
  // state
  const [state, setState] = React.useState({
    logs: [],
    total: 0,
    isLoading: false,
    showFilters: false,
  } as {
    logs: IAuthLog[];
    total: number;
    isLoading: boolean;
    showFilters: boolean;
  });
  const [filters, setFilters] = React.useState<IAuthLogFilters>({
    offset: 0,
    limit: 10,
  });

  // custom hooks
  const { showToast } = React.useContext(ToastContext);

  React.useEffect(() => {
    loadLogs(filters, true);
  }, []);

  const loadLogs = async (currentFilters: IAuthLogFilters, showLoading = false) => {
    if (showLoading) setState(prev => ({ ...prev, isLoading: true }));
    const result = await getAuthLogs(currentFilters);
    if (result?.success) {
      setState(prev => ({
        ...prev,
        logs: result.data.data,
        total: result.data.total,
        isLoading: false,
      }));
      setFilters(currentFilters);
    } else {
      showToast(
        result.message || "Error while getting authentication logs",
        "error"
      );
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleFilterChange = (partial: Partial<IAuthLogFilters>) => {
    const updated = { ...filters, ...partial, offset: 0 };
    loadLogs(updated);
  };

  const handleClearFilters = () => {
    const defaultFilters: IAuthLogFilters = { offset: 0, limit: 10 };
    setFilters(defaultFilters);
    loadLogs(defaultFilters, true);
  };

  const onExport = async () => {
    await exportAuthLogsExcel(filters);
  };

  const _onRenderRow: IDetailsListProps["onRenderRow"] = (props) => {
    const customStyles: Partial<IDetailsRowStyles> = {};
    if (props) {
      if (props.itemIndex % 2 !== 0) {
        customStyles.root = { backgroundColor: "#F3F3F3" };
      }
      return <DetailsRow {...props} styles={customStyles} />;
    }
    return null;
  };

  const _renderItemColumn = (item: any, index: number, column: IColumn) => {
    switch (column.key) {
      case "createdAt":
        return (
          <span style={{ fontWeight: 600 }}>
            {moment(item.createdAt).format("DD/MM/YYYY hh:mm:ss A")}
          </span>
        );
      case "event":
        return (
          <Stack tokens={{ childrenGap: 4 }}>
            <span style={{ color: AUTH_EVENT_COLOR[item.event] || "#323130" }}>
              {AUTH_EVENT_OPTIONS_DICT[item.event] || item.event.replace(/_/g, " ")}
            </span>
            {item.loginMethod && (
              <span style={{ fontSize: 12, color: "#605e5c" }}>{AUTH_LOGIN_METHODS_DICT[item.loginMethod] || item.loginMethod}</span>
            )}
          </Stack>
        );
      case "status":
        return (
          <span style={{ color: AUTH_STATUS_COLOR[item.status] || "#323130" }}>
            {AUTH_STATUS_OPTIONS_DICT[item.status] || item.status}
          </span>
        );
      case "user":
        return (
          <Stack tokens={{ childrenGap: 4 }}>
            <span>{item.userName || "Unknown"}</span>
            <span style={{ fontSize: 12, color: "#605e5c" }}>{item.userEmail || "—"}</span>
          </Stack>
        );
      case "target":
        return (
          item.targetUserId ? (
            <Stack tokens={{ childrenGap: 4 }}>
              <span>{item.targetUserName || "—"}</span>
              <span style={{ fontSize: 12, color: "#605e5c" }}>{item.targetUserEmail || "—"}</span>
            </Stack>
          ) : <span>—</span>
        );
      case "device":
        return (
          <Stack tokens={{ childrenGap: 4 }}>
            <span>
              {[item.os, item.browser].filter(Boolean).join(" / ") || "—"}
            </span>
            <span style={{ fontSize: 12 }}>{item.ipAddress || "—"}</span>
          </Stack>
        );
      case "reason":
        return (
          <span style={{ color: item.reason ? "#a4262c" : "#605e5c" }}>
            {item.reason ? item.reason.replace(/_/g, " ") : "—"}
          </span>
        );
      default:
        return (
          <span>
            {column.fieldName
              ? item[column.fieldName] !== ""
                ? item[column.fieldName]
                : "NA"
              : ""}
          </span>
        );
    }
  };

  return (
    <React.Fragment>
      {state.isLoading && <Loading />}
      <Stack tokens={childrenGap} style={{ marginTop: 16 }}>
        <Stack.Item>
          <Stack horizontal wrap horizontalAlign="space-between" verticalAlign="center" tokens={childrenGap}>
            <Stack.Item>
              <Stack horizontal wrap verticalAlign="center" tokens={childrenGap}>
                <Stack.Item>
                  <SearchBox
                    placeholder="Search user email..."
                    styles={{ root: { width: 300 } }}
                    onChange={(_, val) => handleFilterChange({ search: val })}
                  />
                </Stack.Item>
                <Stack.Item className="filter-btn">
                  <TooltipHost
                    content={"Auth Filters"}
                    id={`authFilter`}
                    calloutProps={{ gapSpace: 0 }}
                    styles={{ root: { display: "inline-block" } }}
                  >
                    <span onClick={() => setState(prev => ({ ...prev, showFilters: !prev.showFilters }))}>
                      <Icon
                        path={mdiFilterOutline}
                        size={1.0}
                        className="pointer"
                        color={THEME.palette.neutralLighterAlt}
                      />
                    </span>
                  </TooltipHost>
                </Stack.Item>
                {state.showFilters && (
                  <>
                    <Stack.Item>
                      <DatePicker
                        placeholder="Start Date"
                        firstDayOfWeek={DayOfWeek.Sunday}
                        showMonthPickerAsOverlay={true}
                        showGoToToday={true}
                        value={filters.startDate ? new Date(filters.startDate) : undefined}
                        onSelectDate={date => handleFilterChange({ startDate: date ? moment(date).format("YYYY-MM-DD") : undefined })}
                        styles={{ root: { width: 160 } }}
                      />
                    </Stack.Item>
                    <Stack.Item>
                      <DatePicker
                        placeholder="End Date"
                        firstDayOfWeek={DayOfWeek.Sunday}
                        showMonthPickerAsOverlay={true}
                        showGoToToday={true}
                        value={filters.endDate ? new Date(filters.endDate) : undefined}
                        onSelectDate={date => handleFilterChange({ endDate: date ? moment(date).format("YYYY-MM-DD") : undefined })}
                        styles={{ root: { width: 160 } }}
                      />
                    </Stack.Item>
                    <Stack.Item>
                      <Dropdown
                        placeholder="Select an event"
                        selectedKey={filters.event || ""}
                        styles={{ root: { width: 160 } }}
                        options={AUTH_EVENT_OPTIONS}
                        onChange={(_, opt) => handleFilterChange({ event: opt?.key as string || undefined })}
                      />
                    </Stack.Item>
                    <Stack.Item>
                      <Dropdown
                        placeholder="Select a status"
                        selectedKey={filters.status || ""}
                        styles={{ root: { width: 160 } }}
                        options={AUTH_STATUS_OPTIONS}
                        onChange={(_, opt) => handleFilterChange({ status: opt?.key as string || undefined })}
                      />
                    </Stack.Item>
                  </>
                )}
                <Stack.Item>
                  <DefaultButton
                    text="Clear"
                    styles={{ root: { height: 32 } }}
                    onClick={handleClearFilters}
                  />
                </Stack.Item>
              </Stack>
            </Stack.Item>
            <Stack.Item>
              <PrimaryButton
                text="Export Excel"
                iconProps={{ iconName: "Download" }}
                onClick={onExport}
                onRenderIcon={() => <Icon path={mdiDownloadOutline} size={0.7} color="white" style={{ marginRight: 8 }} />}
              />
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item>
          <Fabric>
            {state.logs && state.logs.length > 0 ? (
              <DetailsList
                items={state.logs}
                columns={[
                  {
                    key: "createdAt",
                    name: "Date / Time",
                    fieldName: "createdAt",
                    minWidth: 170,
                    maxWidth: 220,
                    isResizable: true,
                  },
                  {
                    key: "event",
                    name: "Event",
                    fieldName: "event",
                    minWidth: 130,
                    maxWidth: 180,
                    isResizable: true,
                  },
                  {
                    key: "status",
                    name: "Status",
                    fieldName: "status",
                    minWidth: 50,
                    maxWidth: 100,
                    isResizable: true,
                  },
                  {
                    key: "user",
                    name: "User",
                    fieldName: "user",
                    minWidth: 160,
                    maxWidth: 200,
                    isResizable: true,
                  },
                  {
                    key: "device",
                    name: "Device / IP",
                    fieldName: "device",
                    minWidth: 180,
                    maxWidth: 230,
                    isResizable: true,
                  },
                  {
                    key: "reason",
                    name: "Reason",
                    fieldName: "reason",
                    minWidth: 250,
                    maxWidth: 300,
                    isResizable: true,
                  },
                  {
                    key: "target",
                    name: "Target User",
                    fieldName: "target",
                    minWidth: 160,
                    maxWidth: 200,
                    isResizable: true,
                  },
                ]}
                compact={true}
                setKey="set"
                onRenderItemColumn={_renderItemColumn as any}
                layoutMode={DetailsListLayoutMode.justified}
                selectionMode={SelectionMode.none}
                onRenderRow={_onRenderRow}
              />
            ) : !state.isLoading && (
              <NoDataTile title="No Authentication Logs Found" />
            )}
          </Fabric>
        </Stack.Item>
        {state.total > 0 && (
          <Stack.Item>
            <CustomPagination
              totalCount={state.total}
              search={{ limit: filters.limit, offset: filters.offset || 0 }}
              name="logs"
              getFrom={(offset: number) => loadLogs({ ...filters, offset })}
            />
          </Stack.Item>
        )}
      </Stack>
    </React.Fragment>
  );
};

export default AuthLogTab;
